/**
 * featureFlags.ts — localStorage-backed feature-flag resolver.
 *
 * Supports `export.pipeline = legacy | v2` switching for the export-pipeline
 * redesign. Default values are hardcoded in FLAG_DEFAULTS; localStorage
 * overrides are applied on top.
 *
 * Refs: docs/superpowers/plans/2026-05-18-export-layout-redesign.md Task 0.1
 */
const FLAG_PREFIX = 'pecktree.flag.';
const FLAG_DEFAULTS: Record<string, string> = {
  // #525 — default 'legacy' voor ONSCREEN tree (useTreeLayout.orchestrator).
  // Plan A's V2 pair-clustering + 90° rotation is ALLEEN bedoeld voor PDF-export
  // ('5 gen op 1 A4'-feature), NIET voor onscreen pedigree-view.
  //
  // PDF-export gebruikt V2 byte-assembly HARDCODED in runExport (Task 7+),
  // dus PDF blijft via V2-pad + rotation ongeacht deze flag.
  'export.pipeline': 'legacy',
  // #586/#562/#1039 — voetafdruk-bewuste partner-spacing op PEDIGREE +
  // DESCENDANTS (applyScreenPartnerSpacing / layoutDescendants-voetafdruk).
  // Default 'on' na visueel akkoord (Twan, 2026-06-02): verandert het gedrag
  // ALLEEN bij personen met 2+ partners; 0/1 partner = identiek aan voorheen.
  // Op 'off' zetten herstelt exact het oude V1-gedrag (fallback).
  // Spec: docs/superpowers/specs/2026-06-02-tree-view-layout-requirements.md §4.5.
  'tree.partnerSpacing': 'on',
  // §9 — relatie-type-lijnen (adoptie/pleeg) + ♂/♀ a11y-glyph + legenda-uitbreiding.
  // Default 'on' na Twan's visueel akkoord (2026-06-02). Puur additief: raakt geen
  // layout-positie. Op 'off' zetten herstelt exact het oude visuele gedrag (kale
  // conn-line, geen glyph). Verandert alleen trees met adoptie/pleeg-relaties + toont
  // de ♂/♀-glyph op alle nodes.
  // Spec: docs/superpowers/specs/2026-06-02-tree-view-layout-requirements.md §9.
  'tree.relationTypeLines': 'on',
  // #1089 — edit-mode "add missing parent" placeholders als first-class layout-nodes.
  // Default 'off': placeholders worden NIET gegenereerd tenzij de flag actief is.
  // Op 'on' zetten voegt placeholder-nodes toe voor ontbrekende ouders in edit-mode.
  // Raakt layout-positie ALLEEN wanneer een ouder ontbreekt in edit-mode.
  'tree.editPlaceholders': 'off',
  // Implex (pedigree-collapse): toon dezelfde voorouder op elke Sosa-positie (volledig
  // herhalen) met ✦-marker. Default 'off': buildSosaMap-dedup blijft actief (1 node per
  // persoon, byte-identieke layout). 'on' = buildSosaSlots-pad (meerdere slots per persoon).
  // Spec: docs/superpowers/specs/2026-06-04-tree-implex-pedigree-collapse-design.md.
  'tree.implex': 'off',
};

export function getFeatureFlag<T extends string>(name: string, fallback: T): T {
  const local = localStorage.getItem(FLAG_PREFIX + name);
  if (local !== null) return local as T;
  return (FLAG_DEFAULTS[name] ?? fallback) as T;
}

export function setFeatureFlag(name: string, value: string): void {
  localStorage.setItem(FLAG_PREFIX + name, value);
}
